import { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { startDesignSession, sendDesignMessage, closeDesignSession } from '../api/badge-design';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
    imageUrl?: string;
}

interface Props {
    open: boolean;
    badgeName?: string;
    badgeDescription?: string;
    onAccept: (imageUrl: string) => void;
    onClose: () => void;
}

// ---------------------------------------------------------------------------
// Design tokens — matches screenshot reference
// ---------------------------------------------------------------------------

const PREVIEW_BG = '#0f1729';           // dark navy for image preview panel
const USER_BUBBLE_BG = '#3d4f7c';       // slate-blue for user messages
const ASSISTANT_BUBBLE_BG = '#f7f8fa';  // very light grey for assistant messages
const PANEL_BORDER = '#e2e5ec';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BadgeDesignAssistant({ open, badgeName, badgeDescription, onAccept, onClose }: Props) {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [allImages, setAllImages] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // -----------------------------------------------------------------------
    // Session lifecycle
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        setMessages([{ role: 'assistant', text: "Hi! I'm your badge design assistant. Give me a moment while I put together some initial ideas for you…" }]);
        setInput('');
        setError(null);
        setSelectedImage(null);
        setAllImages([]);
        setSessionId(null);
        setLoading(true);

        startDesignSession({ badgeName, badgeDescription }).then((res) => {
            if (cancelled) return;
            setLoading(false);
            if (!res.success || !res.data) {
                setError(res.message ?? 'Failed to start design session');
                return;
            }
            setSessionId(res.data.sessionId);
            const msg: ChatMessage = { role: 'assistant', text: res.data.message };
            if (res.data.imageUrl) {
                msg.imageUrl = res.data.imageUrl;
                setAllImages([res.data.imageUrl]);
                setSelectedImage(res.data.imageUrl);
            }
            setMessages([msg]);
        });

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open && sessionId) {
            closeDesignSession(sessionId).catch(() => {});
            setSessionId(null);
        }
    }, [open, sessionId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // -----------------------------------------------------------------------
    // Send
    // -----------------------------------------------------------------------

    const handleSend = async () => {
        const text = input.trim();
        if (!text || !sessionId || loading) return;

        setInput('');
        setError(null);
        setMessages(prev => [...prev, { role: 'user', text }]);
        setLoading(true);

        const res = await sendDesignMessage({ sessionId, message: text });
        setLoading(false);

        if (!res.success || !res.data) {
            setError(res.message ?? 'Something went wrong');
            return;
        }

        const msg: ChatMessage = { role: 'assistant', text: res.data.message };
        if (res.data.imageUrl) {
            msg.imageUrl = res.data.imageUrl;
            setAllImages(prev => [...prev, res.data!.imageUrl!]);
            setSelectedImage(res.data.imageUrl);
        }

        setMessages(prev => [...prev, msg]);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    // -----------------------------------------------------------------------
    // Accept
    // -----------------------------------------------------------------------

    const handleAccept = async () => {
        if (!selectedImage) return;
        // Convert data URL to File without fetch() — avoids CSP connect-src restriction
        const [header, b64] = selectedImage.split(',');
        const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        const file = new File([blob], 'ai-badge.png', { type: mime });
        onAccept(selectedImage);
        const event = new CustomEvent('badge-design-file', { detail: file });
        window.dispatchEvent(event);
        onClose();
    };

    const activeVersion = allImages.length > 0 ? `V${allImages.length}.0` : null;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#ffffff',
                    overflow: 'hidden',
                    borderRadius: 2,
                },
            }}
        >
            {/* ----------------------------------------------------------------
                Header
            ---------------------------------------------------------------- */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2.5,
                py: 1.5,
                borderBottom: `1px solid ${PANEL_BORDER}`,
                bgcolor: '#ffffff',
                flexShrink: 0,
            }}>
                <AutoAwesomeIcon sx={{ color: '#3d4f7c', mr: 1, fontSize: 20 }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
                        {badgeName || 'Badge Design Assistant'}
                    </Typography>
                    {sessionId && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                            DESIGN SESSION ID: {sessionId.slice(0, 9).toUpperCase()}
                        </Typography>
                    )}
                </Box>
                {loading && (
                    <Chip
                        size="small"
                        icon={<FiberManualRecordIcon sx={{ fontSize: '10px !important', color: '#e53935 !important' }} />}
                        label="LIVE PROCESSING"
                        sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, bgcolor: '#fff5f5', color: '#e53935', border: '1px solid #fecdd3', mr: 1.5 }}
                    />
                )}
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* ----------------------------------------------------------------
                Body: left chat | right preview
            ---------------------------------------------------------------- */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* ------------------------------------------------------------
                    Left: Chat
                ------------------------------------------------------------ */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: `1px solid ${PANEL_BORDER}`,
                    minWidth: 0,
                    bgcolor: '#ffffff',
                }}>
                    {/* Messages */}
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {loading && messages.length === 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                <CircularProgress size={16} thickness={5} />
                                <Typography variant="body2" color="text.secondary">Starting session…</Typography>
                            </Box>
                        )}

                        {messages.map((msg, i) => (
                            <Box key={i} sx={{ display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 1 }}>
                                {msg.role === 'assistant' && (
                                    <Avatar sx={{ width: 26, height: 26, bgcolor: '#3d4f7c', flexShrink: 0, mt: 0.25 }}>
                                        <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                                    </Avatar>
                                )}
                                <Box sx={{
                                    maxWidth: '78%',
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
                                    bgcolor: msg.role === 'user' ? USER_BUBBLE_BG : ASSISTANT_BUBBLE_BG,
                                    color: msg.role === 'user' ? '#ffffff' : 'text.primary',
                                    border: msg.role === 'assistant' ? `1px solid ${PANEL_BORDER}` : 'none',
                                }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                                        {msg.text}
                                    </Typography>
                                    {msg.imageUrl && (
                                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', opacity: 0.65 }}>
                                            ↗ Image generated — see preview
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}

                        {loading && messages.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 26, height: 26, bgcolor: '#3d4f7c', flexShrink: 0 }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                                </Avatar>
                                <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center', px: 1.5, py: 1, bgcolor: ASSISTANT_BUBBLE_BG, border: `1px solid ${PANEL_BORDER}`, borderRadius: '2px 12px 12px 12px' }}>
                                    {[0, 1, 2].map(j => (
                                        <Box key={j} sx={{
                                            width: 6, height: 6, borderRadius: '50%', bgcolor: '#aab',
                                            animation: 'pulse 1.2s ease-in-out infinite',
                                            animationDelay: `${j * 0.2}s`,
                                            '@keyframes pulse': { '0%,80%,100%': { opacity: 0.3 }, '40%': { opacity: 1 } },
                                        }} />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {error && (
                            <Typography variant="caption" color="error" sx={{ px: 0.5 }}>{error}</Typography>
                        )}

                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Input bar */}
                    <Box sx={{ p: 2, borderTop: `1px solid ${PANEL_BORDER}`, bgcolor: '#fafbfc' }}>
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            multiline
                            maxRows={3}
                            size="small"
                            placeholder="Describe changes or pick a direction…"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={loading || !sessionId}
                            sx={{
                                mb: 1.25,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#ffffff',
                                    fontSize: 13,
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                onClick={handleSend}
                                disabled={loading || !input.trim() || !sessionId}
                                endIcon={loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <span>▶</span>}
                                sx={{
                                    bgcolor: '#0f1729',
                                    '&:hover': { bgcolor: '#1a2a4a' },
                                    '&:disabled': { bgcolor: '#c5cad4', color: '#fff' },
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    fontSize: 12,
                                    px: 2.5,
                                }}
                            >
                                GENERATE
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* ------------------------------------------------------------
                    Right: Preview + history
                ------------------------------------------------------------ */}
                <Box sx={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>

                    {/* Main image preview — dark navy background */}
                    <Box sx={{ position: 'relative', bgcolor: PREVIEW_BG, flexShrink: 0 }}>
                        <Box sx={{
                            width: '100%',
                            aspectRatio: '1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            {selectedImage ? (
                                <Box component="img" src={selectedImage} alt="Badge preview"
                                    sx={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                            ) : loading ? (
                                <Box sx={{ textAlign: 'center' }}>
                                    <CircularProgress size={28} sx={{ color: '#4a6fa5' }} />
                                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#6b7db3' }}>
                                        Generating…
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="caption" sx={{ color: '#4a5568', textAlign: 'center', px: 3 }}>
                                    Your badge mockup will appear here
                                </Typography>
                            )}
                        </Box>
                        {activeVersion && (
                            <Box sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,0.55)', px: 1, py: 0.25, borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#a0b4d0', fontFamily: 'monospace', fontSize: 10, fontWeight: 700 }}>
                                    {activeVersion} ACTIVE
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Badge name strip */}
                    {selectedImage && (
                        <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${PANEL_BORDER}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{badgeName || 'Badge'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Transparent PNG · {allImages.length > 1 ? `${allImages.length} versions` : '1 version'}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Iterative history */}
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {allImages.length > 0 && (
                            <>
                                <Typography variant="caption" fontWeight={700} letterSpacing={0.8} color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                                    ITERATIVE HISTORY
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
                                    {allImages.map((url, i) => (
                                        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                            <Box
                                                component="img"
                                                src={url}
                                                alt={`V${i + 1}`}
                                                onClick={() => setSelectedImage(url)}
                                                sx={{
                                                    width: 52,
                                                    height: 52,
                                                    borderRadius: 1,
                                                    objectFit: 'contain',
                                                    border: 2,
                                                    borderColor: url === selectedImage ? '#3d4f7c' : PANEL_BORDER,
                                                    cursor: 'pointer',
                                                    bgcolor: '#f0f2f8',
                                                    '&:hover': { borderColor: '#3d4f7c' },
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                                V{i + 1}.0
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Accept strip */}
                    <Box sx={{
                        p: 2,
                        bgcolor: '#1e2d4a',
                        borderTop: `1px solid ${PANEL_BORDER}`,
                    }}>
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<CheckCircleIcon fontSize="small" />}
                            disabled={!selectedImage}
                            onClick={handleAccept}
                            sx={{
                                bgcolor: '#3d4f7c',
                                '&:hover': { bgcolor: '#4a5f96' },
                                '&:disabled': { bgcolor: '#2a3550', color: '#4a5f7c' },
                                fontWeight: 700,
                                fontSize: 12,
                                letterSpacing: 0.5,
                            }}
                        >
                            Use this image
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}
